/**
 * Voicemail Count Hook
 * Custom hook to fetch and manage missed calls and voicemail count
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { voicemailService } from '@/services/features/VoicemailService';

export const useVoicemailCount = () => {
  const [voicemailCount, setVoicemailCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { user, token } = useAuth();

  const fetchVoicemailCount = useCallback(async () => {
    if (!user?.id || !token) {
      setVoicemailCount(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch unread voicemails without filters to get total count
      const response = await voicemailService.fetchUnreadVoicemails(
        user.id,
        { page: 1, size: 1 }, // Minimal pagination since we only need the count
        token
      );
      setVoicemailCount(response.totalItems);
    } catch (err) {
      console.error('Failed to fetch voicemail count:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch voicemail count');
      setVoicemailCount(0);
    } finally {
      setLoading(false);
    }
  }, [user?.id, token]);

  useEffect(() => {
    fetchVoicemailCount();
  }, [fetchVoicemailCount]);

  return {
    voicemailCount,
    loading,
    error,
    refetch: fetchVoicemailCount
  };
};
